import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { toast } from 'react-toastify'

import SendIcon from '@material-ui/icons/Send'
import CancelIcon from '@material-ui/icons/Cancel'
import { MenuItem } from '@material-ui/core'

import { Button } from '../../components/Button'
import { Modal } from '../../components/Modal'
import { TextField } from '../../components/form/TextField'

import { sendMessageToPortalOwner } from '../../api/emails'
import { getDistinctPublishersForPortalUser } from '../../api/receipts'

import styles from '../styles.module.scss'
import { useValidations } from '../../hooks/useValidations'
import { checkErrorText } from '../../utils/getServerErrorCodeText'
import { LoadingScreen } from '../../screens/LoadingScreen'

export const ContactUsModal = ({ onRequestClose, ...props }) => {
  const { t, i18n } = useTranslation('modals', { keyPrefix: 'contact' })
  const validations = useValidations()
  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      publisher: '',
    },
  })

  const portalAbbr = useSelector((state) => state.application.portalAbbr)
  const [isLoading, setIsLoading] = useState(true)
  const [publishers, setPublishers] = useState([])

  const setDefaultPublisher = (defaultPublisher) => {
    setValue('publisher', defaultPublisher)
  }

  const onSubmit = async (data) => {
    let success = false
    let selectedPublisher
    try {
      selectedPublisher = publishers.find((p) => p.name === data.publisher)

      const updatedData = {
        ...data,
        publisher: selectedPublisher?.verlagsnummer,
      }

      await sendMessageToPortalOwner(updatedData, portalAbbr)
      toast.success(i18n.t('notifications:messageSent'), {
        autoClose: 3000,
      })
      success = true
    } catch (error) {
      toast.error(checkErrorText(error.response.data.code), {
        autoClose: 3000,
      })
    } finally {
      reset()
      if (selectedPublisher?.name) {
        setDefaultPublisher(selectedPublisher.name)
      }
      onRequestClose(success)
    }
  }

  useEffect(() => {
    const fetchPublishers = async () => {
      try {
        const response = await getDistinctPublishersForPortalUser(portalAbbr)
        const fetchedPublishers = response.data.data
        if (fetchedPublishers?.length > 0) {
          setPublishers(fetchedPublishers)
          // Set default value once data is loaded
          setDefaultPublisher(fetchedPublishers[0].name)
        }
      } catch (error) {
        toast.error(checkErrorText(error.response.data.code), {
          autoClose: 3000,
        })
      } finally {
        setIsLoading(false)
      }
    }
    fetchPublishers()
  }, [portalAbbr])

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <Modal title={t('title')} {...props}>
      <form
        className={styles.contactFormContainer}
        onSubmit={handleSubmit(onSubmit)}
      >
        <span className={styles.title}>{t('label')}</span>
        <Controller
          name="publisher"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              InputLabelProps={{
                style: {
                  top: '-5px',
                },
              }}
              select
              label={t('publisher')}
              value={value}
              onChange={onChange}
              className={`${styles.input} ${styles.inputLabelAdjust}`}
              error={!!error}
              helperText={error?.message}
            >
              {publishers.map((publisher) => (
                <MenuItem key={publisher.verlagsnummer} value={publisher.name}>
                  {publisher.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          name="subject"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              placeholder={t('subject')}
              className={styles.input}
              value={value}
              onChange={onChange}
              error={!!error}
              helperText={error?.message}
            />
          )}
          rules={validations.requiredField}
        />
        <Controller
          name="message"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              placeholder={t('message')}
              className={styles.input}
              multiline
              rows={10}
              value={value}
              onChange={onChange}
              error={!!error}
              helperText={error?.message}
            />
          )}
          rules={validations.requiredField}
        />
        <div className={styles.controls}>
          <Button
            className={styles.controlButton}
            type="submit"
            variant="contained"
            disableElevation
            startIcon={<SendIcon />}
          >
            {t('accept')}
          </Button>
          <Button
            className={styles.controlButton}
            type="button"
            variant="contained"
            disableElevation
            secondary
            startIcon={<CancelIcon />}
            onClick={() => onRequestClose(false)}
          >
            {t('cancel')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

ContactUsModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
}
